import type { BlockId, BlockStateUnionType } from '@unifyapps/defs/types/block';
import _pick from 'lodash/pick';
import invariant from 'ts-invariant';
import InterfacePageHelper from '../../../../../helper/InterfacePage';
import { parseRuntimeBlockId } from '../../../../../utils/repeatables/parseRuntimeBlockId';
import { getActiveInterfacePageFromStore } from '../../../runner/state';
import type { InterfaceStoreStateGetterAndSetter } from '../../types';

export const nextBlockStateKeys = [
  'appearance',
  'content',
  'slots',
  'events',
  'id',
  'visibility',
  'additional',
];

// Runtime instances reuse the original's re-stamp set minus `id`. The runtime
// ID (`runtime_block_<originalId>_<rowKey>`) must survive — only the template
// slot's `id` equals `originalBlockId`. Everything else (config-driven keys)
// re-stamps identically.
const nextBlockStateKeysForInstance = nextBlockStateKeys.filter((key) => key !== 'id');

export type UpdateBlockStateByActiveDeviceActionProps = {
  blockId: BlockId;
};
/**
 * this function updates the reinitialized the block state by picking the block info from the accurate place in the interface page
 * also after updating the block state, it updates the dependency graph
 * @param storeArgs - store args
 * @param blockId - block id
 */
export const getUpdateBlockStateByActiveDeviceAction =
  (storeArgs: InterfaceStoreStateGetterAndSetter) =>
  ({ blockId }: UpdateBlockStateByActiveDeviceActionProps) => {
    const { get, getDeviceDetails, registry, updateBlockState, getGlobalStateStore } = storeArgs;

    const currentDevice = getDeviceDetails().device;
    const interfacePage = getActiveInterfacePageFromStore(get());

    invariant(blockId, 'blockId is required while updating block state by active device');

    const block = interfacePage
      ? InterfacePageHelper.getBlockFromInterfacePage(interfacePage, currentDevice, blockId)
      : undefined;

    if (!block) {
      console.error(`updateBlockStateByActiveDevice: Block with id ${blockId} not found`);
      return;
    }

    const nextBlockState = registry.getBlockInitialState(block.component.componentType, block);

    if (!nextBlockState) return;

    // update only block state content appearance slots and events as
    // they can only be changed by the user rest state is persisted
    updateBlockState(blockId, (draft) => {
      Object.assign(draft as BlockStateUnionType, _pick(nextBlockState, nextBlockStateKeys));
    });

    // Re-stamp every materialized runtime instance of this block with the same
    // config-driven keys. Without this fanout, Repeatable rows would stay frozen
    // on whatever snapshot materializeInstanceState took at first-write — a
    // template edit (appearance, content, slots, …) wouldn't reach existing rows.
    // The reader (useGetAndSetRunTimeBlockState) used to layer the original
    // block's state under the runtime entry on every read to paper over this;
    // with the fanout in place that overlay is unnecessary.
    const blocksState = getGlobalStateStore().blocks;
    const restampedConfig = _pick(nextBlockState, nextBlockStateKeysForInstance);
    for (const candidateId of Object.keys(blocksState)) {
      const parsed = parseRuntimeBlockId(candidateId);
      if (!parsed.isInstance) continue;
      if (parsed.originalBlockId !== blockId) continue;
      updateBlockState(candidateId, (draft) => {
        Object.assign(draft as BlockStateUnionType, restampedConfig);
      });
    }
  };
